import type {
  WorkbookResult,
  WorkbookColumnType,
  WorkbookSheet,
  WorkbookValidation,
  WorkbookValidationKind,
  WorkbookValidationResult,
} from '@erp/shared-types';
import { isDateColumnType } from '@erp/workbooks';
import { stableSerialize } from '@erp/shared-utils';

type ColumnType = WorkbookColumnType;

/**
 * A write to a Worksheet's Validations or Sample, the parts every kind of
 * Worksheet has: it keeps the Worksheet's kind.
 */
export type SheetResultUpdate = <S extends WorkbookSheet>(sheet: S) => S;
export type ChangeSheetResult = (update: SheetResultUpdate) => void;

/**
 * What the shown Result says about one of the Worksheet's Validations: the
 * Run checked it as it is now (`checked`), checked an earlier version of it
 * (`changed`), or never checked it (`unchecked`).
 */
export type ValidationState =
  | { kind: 'unchecked' }
  | { kind: 'changed' }
  | { kind: 'checked'; check: WorkbookValidationResult };

/** Each Validation's state in the shown Result, matched by id and then by content. */
export function validationStates(
  sheet: WorkbookSheet,
  result: WorkbookResult | undefined,
): Map<string, ValidationState> {
  const checks = new Map(
    (result?.validations ?? []).map((check) => [check.validation.id, check]),
  );
  return new Map(
    (sheet.validations ?? []).map((validation): [string, ValidationState] => {
      const check = checks.get(validation.id);
      if (!check) return [validation.id, { kind: 'unchecked' }];
      return [
        validation.id,
        stableSerialize(check.validation) === stableSerialize(validation)
          ? { kind: 'checked', check }
          : { kind: 'changed' },
      ];
    }),
  );
}

/** Whether a checked Validation failed on any row. */
export function failing(state: ValidationState | undefined): boolean {
  return state?.kind === 'checked' && state.check.failedRowCount > 0;
}

/**
 * A column's Validations at a glance, for its header: how many it has and
 * the worst outcome among them — `error` when an error-severity one fails,
 * `warning` when only warnings fail, `passed` when every one was checked and
 * passed, else `pending` (some are not checked yet). Null without any.
 */
export function columnValidationStatus(
  sheet: WorkbookSheet,
  states: ReadonlyMap<string, ValidationState>,
  column: string,
): {
  count: number;
  failingRules: number;
  outcome: 'error' | 'warning' | 'passed' | 'pending';
} | null {
  const rules = (sheet.validations ?? []).filter(
    (validation) => validation.column === column,
  );
  if (!rules.length) return null;
  const failed = rules.filter((rule) => failing(states.get(rule.id)));
  const outcome = failed.some((rule) => rule.severity === 'error')
    ? 'error'
    : failed.length
      ? 'warning'
      : rules.every((rule) => states.get(rule.id)?.kind === 'checked')
        ? 'passed'
        : 'pending';
  return { count: rules.length, failingRules: failed.length, outcome };
}

/**
 * The kinds a column of `type` can have: a range checks only Numbers and
 * Dates, and In another sheet needs another Worksheet to compare with.
 */
export function validationKindsFor(
  type: ColumnType,
  hasOtherSheets = false,
): WorkbookValidationKind[] {
  const kinds: WorkbookValidationKind[] = [
    'not-empty',
    'unique',
    'accepted-values',
    'range',
    'pattern',
    'no-errors',
    'formula',
    'in-sheet',
  ];
  return kinds.filter(
    (kind) =>
      (kind !== 'range' || type === 'decimal' || isDateColumnType(type)) &&
      (kind !== 'in-sheet' || hasOtherSheets),
  );
}

/**
 * A new Validation of `kind` on `column`, an error until the user says
 * otherwise; In another sheet compares with `other` and only warns.
 */
export function newValidation(
  id: string,
  column: string,
  kind: WorkbookValidationKind,
  other?: { sheetId: string; matchColumn: string },
): WorkbookValidation {
  const rule = { id, column, severity: 'error' as const };
  switch (kind) {
    case 'in-sheet':
      return {
        ...rule,
        kind,
        severity: 'warning',
        sheetId: other?.sheetId ?? '',
        matchColumn: other?.matchColumn ?? column,
        exactlyOnce: false,
      };
    case 'accepted-values':
      return { ...rule, kind, values: [] };
    case 'range':
      return { ...rule, kind };
    case 'pattern':
      return { ...rule, kind, pattern: '' };
    case 'formula':
      return { ...rule, kind, expression: `[${column}] <> ""` };
    default:
      return { ...rule, kind };
  }
}

/** The Worksheet with `validation` added, or replacing the one with its id. */
export function withValidation<S extends WorkbookSheet>(
  sheet: S,
  validation: WorkbookValidation,
): S {
  const validations = sheet.validations ?? [];
  return {
    ...sheet,
    validations: validations.some((item) => item.id === validation.id)
      ? validations.map((item) =>
          item.id === validation.id ? validation : item,
        )
      : [...validations, validation],
  };
}

/** The Worksheet without the Validation `id`. */
export function withoutValidation<S extends WorkbookSheet>(
  sheet: S,
  id: string,
): S {
  return {
    ...sheet,
    validations: (sheet.validations ?? []).filter((item) => item.id !== id),
  };
}

/** The rows failing Validation `id` in the shown Result, as the grid shows them. */
export function failingRowsFocus(
  sheet: WorkbookSheet,
  result: WorkbookResult | undefined,
  id: string | null,
):
  | {
      rows: WorkbookResult['rows'];
      rowNumbers: number[];
      failedRowCount: number;
    }
  | undefined {
  if (!id) return undefined;
  const state = validationStates(sheet, result).get(id);
  if (state?.kind !== 'checked') return undefined;
  return {
    rows: state.check.failedRows.map((row) => row.cells),
    rowNumbers: state.check.failedRows.map((row) => row.row),
    failedRowCount: state.check.failedRowCount,
  };
}

/** What a column's shield says: its rules failing, all passed, or not all checked yet. */
export function validationMarkState(
  status: NonNullable<ReturnType<typeof columnValidationStatus>>,
): {
  says: 'failing' | 'passed' | 'pending';
  one: boolean;
} {
  const says =
    status.failingRules > 0
      ? 'failing'
      : status.outcome === 'passed'
        ? 'passed'
        : 'pending';
  return { says, one: status.count === 1 };
}
